/**
 * SummaryView.jsx - Tracking > Summary (the default view): what needs
 * attention first, then one of two lenses on the same numbers -
 *   Finance             As sold / cost / profit / margin, money in, PO lines,
 *                       expenses, margin analysis (FinanceLens)
 *   Project management  hours and budget used against time, burn and the
 *                       forecast, by task / engineer, latest entries (PmLens)
 * The lens a person picked is remembered on this computer.
 */
import { useState } from 'react';
import { MUTED } from '../quotes/quoteUi';
import { warningsOf } from './trackingInsights';
import { Segmented } from './trackingUi';
import FinanceLens from './FinanceLens';
import PmLens from './PmLens';

const LENS_KEY = 'globalops_tracking_lens';
const readLens = () => {
  try {
    return window.localStorage.getItem(LENS_KEY) || 'finance';
  } catch {
    return 'finance';
  }
};

export function Warnings({ list, go }) {
  if (!list.length) return null;
  const errors = list.filter((w) => w.level === 'error').length;
  return (
    <div
      role="alert"
      aria-label="Needs attention"
      style={{
        border: `1px solid ${errors ? '#FFCDD2' : '#FDE68A'}`,
        background: errors ? '#FFF5F5' : '#FFFBEB',
        borderRadius: 10,
        padding: '10px 14px',
        marginBottom: 14,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: errors ? '#B71C1C' : '#92400E',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 6,
        }}
      >
        Needs attention ({list.length})
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
        {list.map((w) => (
          <li key={w.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, color: '#1F2328' }}>
            <span aria-hidden style={{ color: w.level === 'error' ? '#C62828' : '#B45309', fontWeight: 900 }}>
              {w.level === 'error' ? '!' : '•'}
            </span>
            <span style={{ flex: 1 }}>{w.text}</span>
            {w.go && (
              <button
                type="button"
                onClick={() => go(w.go)}
                style={{
                  border: 0,
                  background: 'none',
                  padding: 0,
                  fontSize: 11.5,
                  fontWeight: 800,
                  color: '#1B5E20',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                }}
              >
                {w.go === 'log' ? 'Fix in Log' : 'Fix in Setup'} &rarr;
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SummaryView({ data, seesCosts, go, today, canSeeInvoices }) {
  const [lens, setLensState] = useState(readLens);
  const setLens = (v) => {
    setLensState(v);
    try {
      window.localStorage.setItem(LENS_KEY, v);
    } catch {
      /* a convenience only */
    }
  };
  const warnings = warningsOf(data, seesCosts);
  return (
    <div>
      <Warnings list={warnings} go={go} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <Segmented
          label="Summary lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'finance', label: 'Finance' },
            { value: 'pm', label: 'Project management' },
          ]}
        />
        <span style={{ fontSize: 11.5, color: MUTED }}>
          {lens === 'finance'
            ? 'What the job is worth, what it costs and what is billed.'
            : 'Hours and budget used against time, the burn and when the budget runs out.'}
        </span>
      </div>
      {lens === 'finance' ? (
        <FinanceLens data={data} seesCosts={seesCosts} go={go} canSeeInvoices={canSeeInvoices} />
      ) : (
        <PmLens data={data} seesCosts={seesCosts} go={go} today={today} />
      )}
    </div>
  );
}
